import React, { useEffect, useState } from 'react';
import { MapPin, Calendar, Users, DollarSign, ChevronRight } from 'lucide-react';
import { api } from '../../services/apiClient';

interface LinkedTrip {
  id: number;
  trip_date: string;
  trip_time: string;
  status: string;
  group_size: number | null;
  language: string;
  client_name: string | null;
  client_price: string | null;
  guide_fee: string | null;
  payment_status: string;
  route_name: string | null;
  route_slug: string | null;
  guide_first_name: string | null;
  guide_last_name: string | null;
}

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  scheduled: { label: 'Zaplanowana', cls: 'bg-yellow-100 text-yellow-800' },
  confirmed: { label: 'Potwierdzona', cls: 'bg-green-100 text-green-800' },
  in_progress: { label: 'W trakcie', cls: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Zakonczona', cls: 'bg-teal-100 text-teal-800' },
  cancelled: { label: 'Anulowana', cls: 'bg-red-100 text-red-700' },
  no_show: { label: 'Nieobecnosc', cls: 'bg-gray-100 text-gray-600' },
};

const PAY_MAP: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Oczekuje', cls: 'text-amber-600' },
  paid: { label: 'Oplacona', cls: 'text-green-600' },
  refunded: { label: 'Zwrot', cls: 'text-red-600' },
  partial: { label: 'Czesciowo', cls: 'text-orange-600' },
};

export const TripLinkedTripsPanel: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [trips, setTrips] = useState<LinkedTrip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/wycieczki/application/${applicationId}/trips`)
      .then(r => setTrips(r.data.trips || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applicationId]);

  if (loading) return <div className="bg-white rounded-lg shadow p-6 text-gray-400 text-sm">Ładowanie wycieczek...</div>;
  if (trips.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-3 flex items-center gap-2">
        <MapPin size={18} className="text-white" />
        <h3 className="text-white font-bold text-sm">Powiazane wycieczki ({trips.length})</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {trips.map(trip => {
          const st = STATUS_MAP[trip.status] || { label: trip.status, cls: 'bg-gray-100 text-gray-700' };
          const pay = PAY_MAP[trip.payment_status] || { label: trip.payment_status, cls: 'text-gray-500' };
          const guide = [trip.guide_first_name, trip.guide_last_name].filter(Boolean).join(' ');
          const date = trip.trip_date ? new Date(trip.trip_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
          const price = trip.client_price ? `${Number(trip.client_price).toLocaleString('pl-PL')} zl` : '—';

          return (
            <div key={trip.id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-800">{trip.route_name || 'Wycieczka'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                </div>
                <span className={`text-xs font-medium ${pay.cls}`}>{pay.label}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Calendar size={12} />{date}{trip.trip_time ? ` ${trip.trip_time.slice(0, 5)}` : ''}</span>
                {trip.group_size && <span className="flex items-center gap-1"><Users size={12} />{trip.group_size} os.</span>}
                {guide && <span>Przewodnik: {guide}</span>}
                <span className="flex items-center gap-1"><DollarSign size={12} />{price}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
